package fr.frux.core.network.api

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.network.ApiPaths
import fr.frux.shared.dto.SpeciesDto
import fr.frux.shared.dto.SpeciesListResponse
import fr.frux.shared.dto.SpeciesSuggestionRequest
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.RedirectResponseException
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.etag

/**
 * Resultat d'un chargement du referentiel : soit le contenu complet avec l'ETag servi par le
 * backend, soit la confirmation que l'ETag rejoue est toujours valide (304, corps epargne).
 */
sealed interface SpeciesFetch {
    data class Fetched(
        val species: List<SpeciesDto>,
        val etag: String?,
    ) : SpeciesFetch

    data object NotModified : SpeciesFetch
}

/**
 * Referentiel d'especes. Il tient en un appel et change rarement : la recherche reste locale sur
 * la liste en memoire, sans pagination ni endpoint dedie. Le repository singleton ne rappelle pas
 * ce endpoint pendant une session ; entre les sessions, la revalidation conditionnelle rejoue
 * l'ETag du cache persistant (#177) en `If-None-Match`.
 */
interface SpeciesApi {
    suspend fun getSpecies(etag: String? = null): SpeciesFetch

    /**
     * Boite a suggestions d'especes (#233) : journal libre, distinct du circuit structure de
     * proposition. 201 sans corps en nominal ; le serveur peut rejeter en 400
     * (SUGGESTION_NAME_INVALID / SUGGESTION_COMMENT_TOO_LONG) ou 429 metier
     * (SUGGESTION_QUOTA_EXCEEDED), traduits par le client en exception (ClientRequestException).
     */
    suspend fun suggestSpecies(
        name: String,
        comment: String? = null,
    )
}

@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class SpeciesApiImpl(
    private val httpClient: HttpClient,
) : SpeciesApi {
    override suspend fun getSpecies(etag: String?): SpeciesFetch =
        try {
            val response =
                httpClient.get("${ApiPaths.V1}/species") {
                    etag?.let { header(HttpHeaders.IfNoneMatch, it) }
                }
            SpeciesFetch.Fetched(response.body<SpeciesListResponse>().species, response.etag())
        } catch (e: RedirectResponseException) {
            // expectSuccess = true traite tout 3xx en erreur : le 304 attendu ici est le seul cas
            // legitime, et seulement si nous avons vraiment rejoue un ETag.
            if (etag != null && e.response.status == HttpStatusCode.NotModified) {
                SpeciesFetch.NotModified
            } else {
                throw e
            }
        }

    override suspend fun suggestSpecies(
        name: String,
        comment: String?,
    ) {
        httpClient.post("${ApiPaths.V1}/species/suggestions") {
            setBody(SpeciesSuggestionRequest(name, comment))
        }
    }
}
